import { Component, OnInit, inject } from '@angular/core';
import { ChartData } from '../_shared';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { DateIntervalFilterData, AccountFilterData, WidgetFilterData } from '../../_shared/models';
import { DateFilterComponent, AccountFilterComponent } from '../../_shared/components';
import { DateInterval } from '../../_shared/enums';
import { MovingTimeBigModal } from './components/modals/moving-time-big-modal/moving-time-big-modal.component';
import { FrequencyVolumeBigModal } from './components/modals/frequency-volume-big-modal/frequency-volume-big-modal.component';
import { FuelConsumptionModal } from './components/modals/fuel-consumption-modal/fuel-consumption-modal.component';
import { FuelConsumptionBreakdown } from './components/modals/fuel-consumption-breakdown/fuel-consumption-breakdown.component';
import { ReconciliationVariateBreakdown } from './components/modals/reconciliation-variate-breakdown/reconciliation-variate-breakdown.component';
import { FuelCostBreakdown } from './components/modals/fuel-cost-breakdown/fuel-cost-breakdown.component';
import { FuelConsumptionWidgetComponent } from "./components/widget/fuel-consumption-widget/fuel-consumption-widget.component";
import { ReconciliationVariantWidgetComponent } from "./components/widget/reconciliation-variant-widget/reconciliation-variant-widget.component";
import { isEmptyObject } from '../../_shared/helpers/object.helper';
import { FilterService } from '../../_shared/services';
import { getSelectedFilterLabel } from '../../_shared/helpers/kpiWidget/selected-filter.helper';
import { FuelConsumptionWidgetGraphComponent } from "./components/widget/fuel-consumption-widget-graph/fuel-consumption-widget-graph.component";
import { TotalDecreaseWidgetComponent } from "./components/widget/total-decrease-widget/total-decrease-widget/total-decrease-widget.component";
import { FuelCostWidgetComponent } from "./components/widget/fuel-cost-widget/fuel-cost-widget.component";
import { TotalDecreaseWidgetGraphComponent } from "./components/widget/total-decrease-widget-graph/total-decrease-widget-graph.component";
import { ReconciliationVariantWidgetGraphComponent } from "./components/widget/reconciliation-variant-widget-graph/reconciliation-variant-widget-graph.component";
import { FuelCostWidgetGraphComponent } from './components/widget/fuel-cost-widget-graph/fuel-cost-widget-graph.component';
import { FuelDashboardMapComponent } from "./components/fuel-dashboard-map/fuel-dashboard-map.component";
import { FuelChangesClusterRenderer } from './helpers/maps/fuel-changes-cluster-renderer.helper';
import { FuelChangesMapInfoWindowBuilder } from './helpers/maps/fuel-changes-map-info-window-builder.helper';
import { FuelChangesMarkerFactory } from './helpers/maps/fuel-changes-marker-factory.helper';
import { IClusterRenderer } from '../../_shared/interfaces/maps/base-cluster-renderer';
import { IMarkerFactory } from '../../_shared/interfaces/maps/base-marker-factory';
import { BaseMapInfoWindowBuilder } from '../../_shared/interfaces/maps/base-map-info-window-builder';
import { FuelChangesMapService } from './services/fuel-changes-map.service';
import { AccountDateFilterRequest } from '../../_shared/models/accountDateFilter.model';
import { Subject } from 'rxjs';
import { CommonMapService } from '../../_shared/services/map/common-map.service';
import { ErrorHandlerService } from '../../_shared/services/error-handler.service';
import { BasePageComponent } from '../../_shared/components/page/base-page.component';
import { buildDateFilterFromPageFilter } from '../../_shared/helpers/page-filter-state-helper';
import { CommonModule } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    DateFilterComponent,
    AccountFilterComponent,
    FuelConsumptionWidgetComponent,
    ReconciliationVariantWidgetComponent,
    FuelConsumptionWidgetGraphComponent,
    TotalDecreaseWidgetComponent,
    FuelCostWidgetComponent,
    TotalDecreaseWidgetGraphComponent,
    ReconciliationVariantWidgetGraphComponent,
    FuelCostWidgetGraphComponent,
    FuelDashboardMapComponent,
    TranslocoPipe
  ],
  templateUrl: './fuel-dashboard.component.html',
  styleUrl: './fuel-dashboard.component.scss'
})
export class FuelDashboardComponent extends BasePageComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  isLoading: boolean = true;
  isLoadingLocations: boolean = true;
  fuelChangesLocations: any[] = [];
  public dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  public accountFilter: AccountFilterData = {} as AccountFilterData;
  public defaultInterval: DateInterval = DateInterval.Month;
  public disabledIntervals: DateInterval[] = [DateInterval.Half];
  mapCenter: { lat: number, lng: number } = { lat: 0, lng: 0 };
  clusterRenderer: IClusterRenderer = new FuelChangesClusterRenderer();
  markerFactory: IMarkerFactory = new FuelChangesMarkerFactory();
  infoWindowBuilder: BaseMapInfoWindowBuilder = new FuelChangesMapInfoWindowBuilder(key => this.transloco.translate(key));

  protected idleMovingTimeData = ChartData.movingVsIdleTime;
  protected fillUpFrequencyData = ChartData.fillUpFrequency;
  protected accelerationBrakingData = ChartData.accelerationBraking;
  protected distanceDrivenchartData: any[] = ChartData.distanceDriven;
  protected distanceDrivenConfig = ChartData.distanceDrivenConfig
  private cancelSubjectLocations: Subject<void> = new Subject<void>();

  constructor(
    private modalService: NgbModal,
    private filterService: FilterService,
    private fuelChangesMapService: FuelChangesMapService,
    private errorService: ErrorHandlerService,
    private mapDataService: CommonMapService) {
    super();
  }

  override ngOnInit(): void {
    this.mapDataService.updateData([]);
    super.ngOnInit();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelSubjectLocations.next();
    this.cancelSubjectLocations.complete();
  }

  protected override getFilters() {
    return {
      ...this.accountFilter,
      ...this.dateFilter,
    }
  }

  protected override applyPageFilterState(pageFilter: any): void {
    // Apply date filter
    this.dateFilter = buildDateFilterFromPageFilter(this.dateFilter, pageFilter);

    // Apply account filter
    this.accountFilter = { accountIds: pageFilter.accountIds } as AccountFilterData;
  }

  onFuelDashboardDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.accountFilter) && !isEmptyObject(this.dateFilter)) {
      this.filterService.updateFilter(this.getFilters() as WidgetFilterData);
      this.updatePageFilterState();
      this.getFuelChangesLocations();
    }
  }

  async getFuelChangesLocations(): Promise<void> {
    this.isLoadingLocations = true;

    try {
      const request: AccountDateFilterRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
      }

      this.cancelSubjectLocations.next();
      const response = await this.fuelChangesMapService.getFuelChangesLocations(request, this.cancelSubjectLocations);
      this.fuelChangesLocations = response.data?.map(item => {
        return {
          assetId: item.assetId,
          vehicle: item.assetName,
          driver: `${item.firstName} ${item.lastName}`,
          amount: item.amount,
          cost: item.cost,
          type: item.fuelStatusType,
          lat: item.lat,
          lng: item.lng,
          location: item.location,
          timestamp: item.timestamp,
          tank: item.tank,
          fuelConsumption: item.fuelConsumption
        };
      }) ?? [];

      this.mapDataService.updateData(this.fuelChangesLocations);
      this.isLoadingLocations = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingLocations = false;
        }
      });
    }
  }

  fuelConsumptionModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };
    const modalRef = this.modalService.open(FuelConsumptionModal, modalOptions);
    modalRef.componentInstance.value = 100;  // send data to the modal
  }

  fuelBreakDownModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };
    const modalRef = this.modalService.open(FuelConsumptionBreakdown, modalOptions);
  }

  reconciliationModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };
    const modalRef = this.modalService.open(ReconciliationVariateBreakdown, modalOptions);
  }

  fuelCostBreakdownModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };
    const modalRef = this.modalService.open(FuelCostBreakdown, modalOptions);
  }

  protected maximizeGarph(chartData: any, id: string): void {
    // this.dialog
    //   .open(FuelConsumptionGraphModal, {
    //     panelClass: ['bigModal_common', 'homePageModal'],
    //     data: { chartData: chartData, id },
    //   })
    //   .afterClosed();
  }

  protected movingTimeBigModal(): void {
    // this.dialog.open(MovingTimeBigModal, {
    //   panelClass: ['bigModal_common', "movingTimeBigModal"]
    // })

    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };
    const modalRef = this.modalService.open(MovingTimeBigModal, modalOptions);
  }

  getSelectedFilterLabel(filter: DateIntervalFilterData): string {
    return getSelectedFilterLabel(filter, (key, params) => this.transloco.translate(key, params), this.transloco.getActiveLang());
  }

  protected frequencyVolumeBigModal(): void {
    // this.dialog.open(FrequencyVolumeBigModal, {
    //   panelClass: ['bigModal_common', "movingTimeBigModal",]
    // })

    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };
    const modalRef = this.modalService.open(FrequencyVolumeBigModal, modalOptions);
  }

  get selectedFilters(): DateIntervalFilterData & AccountFilterData {
    const filter = {
      ...this.accountFilter,
      ...this.dateFilter,
    } as DateIntervalFilterData & AccountFilterData;
    return filter;
  }

  protected buttonMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  protected onRefresh(): void {
    this.updateFilter();
  }

  protected override shouldRefresh(): boolean {
    return !isEmptyObject(this.accountFilter) && !isEmptyObject(this.dateFilter);
  }
}